/**
 * ============================================================================
 * PORTS — the data-access interface
 * ============================================================================
 *
 * Every read the application performs goes through one of these interfaces.
 * Two implementations exist:
 *
 *   src/data/static/  in-memory fixtures            (today)
 *   src/data/http/    fetch against the REST API    (next)
 *
 * `src/data/index.ts` picks one. Nothing else in the codebase may import an
 * adapter directly — an ESLint rule enforces that.
 *
 * Every method is async even though the static adapter resolves immediately.
 * That is the whole point of the split: the screens already handle pending and
 * error states, so turning on the API changes one environment variable and no
 * component code. See docs/ADR-0002.
 */

import type {
  AccountSnapshot,
  VendorAssessmentSnapshot,
  VendorOnboardingRequestDraft,
  VendorOnboardingRequestReceipt,
  VendorRegisterHit,
  VendorRegisterStatus,
  VendorRegisterType,
  ActivationDataset,
  ActivationDeepDive,
  ActivationDemo,
  ActivationReport,
  ActivationSection,
  ActivationVocabulary,
  Analysis,
  BetaRegistration,
  CatalogueModule,
  DatasetDefectProfile,
  DatasetShape,
  DqsAgentSummary,
  DqsDataset,
  DqsScope,
  ExternalReport,
  HumanLayerProfile,
  HumanLayerProfileDraft,
  HumanLayerVisionProfile,
  HumanLayerVisionProfileDraft,
  HumanLayerVoiceProfile,
  HumanLayerVoiceProfileDraft,
  Id,
  MeasurementChannel,
  MmmActivity,
  MmmActivityQuery,
  MmmActivityYearCount,
  MmmChannelConfig,
  MmmContractEntitlements,
  MmmContractRecord,
  MmmCostModel,
  MmmCountry,
  MmmDqsAgent,
  MmmDqsResult,
  MmmEntitlement,
  MmmGeoNode,
  MmmMetric,
  MmmOutput,
  MmmPartnership,
  MmmRateCard,
  MmmRateCardRevision,
  MmmReport,
  MmmScopeQuery,
  MmmSourceOption,
  MmmSpend,
  MmmSpendBasis,
  MmmSpendFile,
  MmmSpendTemplate,
  MmmSpendUpload,
  MmmVisibilityChannel,
  Pin,
  PinDraft,
  PriceBookItem,
  PricingReference,
  QaFamily,
  QaRule,
  ResearchVocabulary,
  ServiceQuote,
  ServiceQuoteApproval,
  ServiceScope,
  SessionUser,
  SrConfiguration,
  SrContract,
  SrPartner,
  StoredDocument,
  StoredDocumentMeta,
  ZoneCopy,
} from './contracts';

/**
 * Raised when a port method has no implementation in the active adapter.
 * The HTTP adapter throws this for every endpoint the API does not serve yet,
 * which keeps the missing surface visible instead of silently empty.
 */
export class NotImplementedError extends Error {
  constructor(method: string) {
    super(
      `${method} is not implemented by the active data adapter. ` +
        `Set VITE_DATA_SOURCE=static, or implement it in src/data/http.`,
    );
    this.name = 'NotImplementedError';
  }
}

/** Raised by the HTTP adapter for a non-2xx response. */
export class DataRequestError extends Error {
  /** HTTP status, or 0 for a transport-level failure. */
  readonly status: number;
  readonly url: string;

  // Fields are declared and assigned explicitly rather than as constructor
  // parameter properties, which `erasableSyntaxOnly` disallows.
  constructor(message: string, status: number, url: string) {
    super(message);
    this.name = 'DataRequestError';
    this.status = status;
    this.url = url;
  }
}

/* --------------------------------------------------------------------------- */

/** The agent and report catalogue, plus the shell's static copy. */
export interface CataloguePort {
  listModules(): Promise<readonly CatalogueModule[]>;
  listZones(): Promise<readonly ZoneCopy[]>;
  getSessionUser(): Promise<SessionUser>;
  /**
   * The signed-in user's grants and profile.
   *
   * One read, because they are shown together and a page that renders half an
   * account is worse than one that waits. Writes are deliberately absent: access
   * is granted elsewhere, and profile edits land in the session store until the
   * account API exists (DEC-39).
   */
  getAccount(): Promise<AccountSnapshot>;
}

/**
 * Saved analyses. Writes exist because agents save their runs; today they land
 * in the session store, and the same calls will become POST/PUT.
 */
export interface LibraryPort {
  listAnalyses(): Promise<readonly Analysis[]>;
  saveAnalysis(analysis: Analysis): Promise<Analysis>;
  deleteAnalysis(id: Id): Promise<void>;
}

/** Third-party research bench. */
export interface ResearchPort {
  listExternalReports(): Promise<readonly ExternalReport[]>;
  getVocabulary(): Promise<ResearchVocabulary>;
  uploadExternalReport(report: ExternalReport): Promise<ExternalReport>;
}

/**
 * Locally stored documents — the actual uploaded files.
 *
 * Backed by IndexedDB in both adapters, because this is browser storage rather
 * than a server resource. `isAvailable` exists so a caller can distinguish
 * "no documents" from "this browser will not store documents", which are very
 * different things to show a user.
 */
export interface DocumentsPort {
  listDocuments(): Promise<readonly StoredDocumentMeta[]>;
  getDocument(id: Id): Promise<StoredDocument | undefined>;
  putDocument(document: StoredDocument): Promise<StoredDocumentMeta>;
  deleteDocument(id: Id): Promise<void>;
  usedBytes(): Promise<number>;
  isAvailable(): boolean;
}

/**
 * The Marketplace vendor register — the SUPPLY half of a confidence judgement.
 *
 * Read-only from here, and deliberately so: no consumer may create a vendor,
 * and there is no free-text vendor anywhere downstream. If a vendor is not in
 * the register the only path forward is an onboarding request, and the source
 * stays unmapped and reports as a gap in the meantime. An unassessed vendor has
 * to cost something visible or the gate is theatre.
 *
 * `getAssessment` returns a snapshot. A DCS session stores the snapshot and
 * scores on it; a newer approved assessment raises a drift flag and offers a
 * re-read rather than silently rescoring.
 */
export interface VendorRegisterPort {
  search(
    query: string,
    filters?: {
      readonly channelId?: Id;
      readonly metricId?: Id;
      readonly registerType?: VendorRegisterType;
      readonly status?: VendorRegisterStatus;
    },
  ): Promise<readonly VendorRegisterHit[]>;
  /** Null when the solution has no approved or due assessment to read. */
  getAssessment(solutionId: Id): Promise<VendorAssessmentSnapshot | null>;
  createOnboardingRequest(
    draft: VendorOnboardingRequestDraft,
  ): Promise<VendorOnboardingRequestReceipt>;
  getOnboardingRequest(requestId: Id): Promise<VendorOnboardingRequestReceipt>;
}

/** Measurement scope: channels and the remediation price book. */
export interface MeasurementPort {
  listChannels(): Promise<readonly MeasurementChannel[]>;
  listPriceBook(): Promise<readonly PriceBookItem[]>;
}

/**
 * Data-quality reference data and the row source the rule engine runs over.
 *
 * `getDatasetRows` is the method that changes most when the API lands: today it
 * synthesises rows deterministically, tomorrow it pages real ones. The domain
 * rule engine takes rows as an argument and does not care which.
 */
export interface DataQualityPort {
  listFamilies(): Promise<readonly QaFamily[]>;
  listRules(): Promise<readonly QaRule[]>;
  listDatasets(): Promise<readonly DqsDataset[]>;
  listScopes(): Promise<readonly DqsScope[]>;
  listAgentSummaries(): Promise<readonly DqsAgentSummary[]>;
  getDatasetShape(datasetId: Id): Promise<DatasetShape>;
  getDefectProfile(datasetId: Id): Promise<DatasetDefectProfile>;
}

/**
 * MMM builder reference data, worked outputs, and the variable-creation reads.
 *
 * The second block is what the 14-step builder needs. It lives here rather than on an
 * interface of its own so that the adapter split, the `no-restricted-imports` rule and
 * the query-hook pattern all apply to it unchanged — and so the HTTP adapter throws
 * `NotImplementedError` for each one until the API serves it, which keeps the missing
 * surface visible instead of silently empty.
 */
export interface MmmPort {
  listPartnerships(): Promise<readonly MmmPartnership[]>;
  listChannelConfig(): Promise<readonly MmmChannelConfig[]>;
  getCostModel(): Promise<MmmCostModel>;
  listSources(): Promise<readonly MmmSourceOption[]>;
  listMetrics(): Promise<readonly MmmSourceOption[]>;
  getOutput(id: Id): Promise<MmmOutput>;
  listOutputs(): Promise<readonly MmmOutput[]>;

  /* ---- variable creation ---- */

  /** ACCESS-MANAGED. Returns only the contracts the signed-in user may see. */
  listContractRecords(): Promise<readonly MmmContractRecord[]>;
  listCountries(): Promise<readonly MmmCountry[]>;
  /** One level down. The tree is walked on demand — 300 DMAs is not a payload. */
  listGeoChildren(parentId: Id): Promise<readonly MmmGeoNode[]>;
  /**
   * Resolve a saved selection back to its labels.
   *
   * A stored selection keeps ids only. Names are read, never copied, so a platform
   * rename reaches the screen instead of being shadowed by a stale local label.
   */
  readGeoNodes(ids: readonly Id[]): Promise<readonly MmmGeoNode[]>;
  listVisibilityChannels(): Promise<readonly MmmVisibilityChannel[]>;
  listMetricDefinitions(): Promise<readonly MmmMetric[]>;
  listEntitlementDefinitions(): Promise<readonly MmmEntitlement[]>;
  /** The entitlement pull. Proposes channels; it does not confirm them. */
  readEntitlements(contractIds: readonly Id[]): Promise<readonly MmmContractEntitlements[]>;
  listActivities(query: MmmActivityQuery): Promise<readonly MmmActivity[]>;
  /**
   * A count query — deliberately not `array.length` on a fetched page.
   *
   * The years are NOT a parameter. They are enumerated from the analysis window the
   * query already carries, which is what settles calendar-year versus season: the
   * period defined at scope setup leads, and no caller can invent a different one.
   */
  countActivitiesByYear(query: MmmScopeQuery): Promise<readonly MmmActivityYearCount[]>;
  /** From the document system. The report TYPES arrive with the data, not from here. */
  listReports(query: MmmScopeQuery): Promise<readonly MmmReport[]>;
  /**
   * The eventual platform read, from activation management and contract management.
   * No system serves it yet, so it throws — deliberately. Spend arrives by upload
   * until it does, and this stays as the shape that integration must satisfy.
   */
  listSpend(query: MmmScopeQuery): Promise<readonly MmmSpend[]>;
  /** The template the user downloads before filling it in. */
  readSpendTemplate(): Promise<MmmSpendTemplate>;
  /**
   * Parse and validate an uploaded spend file.
   *
   * `basis` is declared by the user, not guessed from the file, and lands on every
   * row. Rejected lines come back in the result rather than being dropped.
   */
  parseSpendUpload(file: MmmSpendFile, basis: MmmSpendBasis): Promise<MmmSpendUpload>;
  /** Joined on `runId` alone. Returns `not_run` for an agent with no assessment. */
  findDqsResults(
    runId: Id,
    agentsInScope: readonly MmmDqsAgent[],
  ): Promise<readonly MmmDqsResult[]>;
  /** Returns the card WITH the version a write has to present. */
  readRateCard(): Promise<MmmRateCardRevision>;
  /**
   * Administrator-only, and the gate belongs on this endpoint rather than on a UI
   * flag. A write carrying a stale `version` is rejected, so two administrators
   * editing within the same hour cannot silently overwrite each other.
   */
  writeRateCard(card: MmmRateCard, version: string): Promise<MmmRateCardRevision>;
}

/** Strategic relevance configuration and assessed partnerships. */
export interface StrategicRelevancePort {
  getConfiguration(): Promise<SrConfiguration>;
  listPartners(): Promise<readonly SrPartner[]>;
  listContracts(): Promise<readonly SrContract[]>;
}

/** Pricing agent reference tables. Prices a CLIENT's rights — see below for ours. */
export interface PricingPort {
  getReference(): Promise<PricingReference>;
}

/**
 * The seam to the Partnership Net Pricing Engine (DEC-48).
 *
 * Scope out, quote back. An agent knows what the client configured; only the
 * engine knows what it costs, so the Arena renders what it is handed.
 *
 * THERE IS NO WRITE FOR A RATE, AND THAT IS THE POINT. Rates, discounts and
 * sequencing belong to one owner; a method here that could change one would make
 * the Arena a second source of truth for a number that appears on an invoice.
 * `acceptQuote` is the only write and it records an acceptance against a quote
 * id — so what the client approved is exactly what they were shown, not whatever
 * the engine would return if asked again a minute later.
 */
export interface ServicePricingPort {
  quote(scope: ServiceScope): Promise<ServiceQuote>;
  acceptQuote(quoteId: string, approvedBy: string): Promise<ServiceQuoteApproval>;
}

/** Activation reports: the library, its vocabulary and the worked demos. */
export interface ActivationPort {
  listReports(): Promise<readonly ActivationReport[]>;
  saveReport(report: ActivationReport): Promise<ActivationReport>;
  listSections(): Promise<readonly ActivationSection[]>;
  listDeepDives(): Promise<readonly ActivationDeepDive[]>;
  listDatasets(): Promise<readonly ActivationDataset[]>;
  getVocabulary(): Promise<ActivationVocabulary>;
  getDemo(id: Id): Promise<ActivationDemo>;
}

/**
 * Beta demand: who asked for which unbuilt module.
 *
 * Two methods and no delete. A registration is a statement of interest someone
 * made, not a setting they can toggle - withdrawing it is an erasure request
 * against the personal-data register, handled there rather than by a button.
 */
export interface BetaPort {
  listRegistrations(): Promise<readonly BetaRegistration[]>;
  register(registration: BetaRegistration): Promise<BetaRegistration>;
}

/**
 * Pinned shortcuts on the shell rail — what the signed-in user chose to keep
 * one click away. See `Pin`'s docblock in contracts.ts for why the record is
 * an id and nothing else.
 *
 * `pin` and `reorder` return the full, authoritative set after the write, the
 * same pattern `writeRateCard` uses and for the same reason: the caller
 * replaces its local state with the adapter's answer rather than patching it
 * by hand, so the two cannot drift apart after a call that half-succeeds.
 *
 * Today only `zone`, `module` and `library-entry` are pinnable from the UI —
 * `search`, `chat`, `alerts` and `create-action` exist on the contract ahead
 * of the surfaces that will offer them (PIN-REGISTRY-AUDIT.md, decision 1).
 */
export interface PinsPort {
  listPins(): Promise<readonly Pin[]>;
  pin(draft: PinDraft): Promise<Pin>;
  unpin(id: Id): Promise<void>;
  reorder(orderedIds: readonly Id[]): Promise<readonly Pin[]>;
}

/**
 * The signed-in user's Human Layer profile: identity and working preferences
 * that shape how agents write for them (Settings → Human Layer → My Profile).
 *
 * DELIBERATELY SEPARATE from `CataloguePort.getAccount()` and the `UserProfile`
 * it returns — contact identity versus AI-personalisation identity are unrelated
 * concepts that happen to both describe "the signed-in user." See
 * `HumanLayerProfile`'s docblock in contracts.ts for the full reasoning; nothing
 * here reuses or extends the account surface, and nothing there should reuse or
 * extend this one.
 *
 * No Zustand store fronts this, for the same reason `PinsPort` above has none:
 * the query cache is the only client-side copy, `updateProfile` returns the
 * full authoritative record the way `pin`/`reorder`/`writeRateCard` do, and the
 * mutation hook writes that result straight into the cache instead of
 * refetching (see PIN-REGISTRY-AUDIT.md's "where pins are stored" section for
 * the argument this follows).
 *
 * `getProfile` never rejects with "not found": a person who has never opened
 * My Profile still gets back a `HumanLayerProfile` with `status:
 * 'not_configured'` and placeholder defaults, because a settings screen with
 * nothing to render is a harder bug to notice than one showing sane defaults.
 */
export interface HumanLayerProfilePort {
  getProfile(): Promise<HumanLayerProfile>;
  updateProfile(draft: HumanLayerProfileDraft): Promise<HumanLayerProfile>;
}

/**
 * The signed-in user's Human Layer voice profile: how they write, captured
 * once so agents draft in a voice that sounds like them (Settings → Human
 * Layer → My Voice).
 *
 * FIRST SLICE. `updateVoiceProfile` only ever constructs a `pasted_text`
 * profile — see `HumanLayerVoiceProfileDraft`'s docblock in contracts.ts. Same
 * no-store, query-cache-only pattern as `HumanLayerProfilePort` above — see
 * that interface's docblock for the full reasoning, which applies unchanged
 * here.
 *
 * `getVoiceProfile` never rejects with "not found", same reasoning as
 * `getProfile`: a person who has never opened My Voice still gets back a
 * `HumanLayerVoiceProfile` with `status: 'not_configured'` and neutral
 * placeholder attributes, because a settings screen with nothing to render is
 * a harder bug to notice than one showing sane defaults.
 */
export interface HumanLayerVoicePort {
  getVoiceProfile(): Promise<HumanLayerVoiceProfile>;
  updateVoiceProfile(draft: HumanLayerVoiceProfileDraft): Promise<HumanLayerVoiceProfile>;
}

/**
 * The signed-in workspace's Human Layer vision profile: what this entity
 * stands for, captured once so agents write with that understanding
 * (Settings → Human Layer → My Vision). See `contracts.ts`'s "human layer:
 * vision" docblock for what this slice deliberately is and is not.
 *
 * Same no-store, query-cache-only pattern as `HumanLayerProfilePort` and
 * `HumanLayerVoicePort` above, and the same "never rejects with not found"
 * rule — a person who has never opened My Vision still gets back a
 * `HumanLayerVisionProfile` with `status: 'not_configured'` and an empty
 * statement list.
 */
export interface HumanLayerVisionPort {
  getVisionProfile(): Promise<HumanLayerVisionProfile>;
  updateVisionProfile(draft: HumanLayerVisionProfileDraft): Promise<HumanLayerVisionProfile>;
}

/**
 * The composed surface. A single object so components take one dependency and
 * tests can substitute a partial fake.
 */
export interface DataAdapter {
  readonly catalogue: CataloguePort;
  readonly beta: BetaPort;
  readonly library: LibraryPort;
  readonly research: ResearchPort;
  readonly documents: DocumentsPort;
  readonly measurement: MeasurementPort;
  readonly vendorRegister: VendorRegisterPort;
  readonly dataQuality: DataQualityPort;
  readonly mmm: MmmPort;
  readonly strategicRelevance: StrategicRelevancePort;
  readonly pricing: PricingPort;
  readonly servicePricing: ServicePricingPort;
  readonly activation: ActivationPort;
  readonly pins: PinsPort;
  readonly humanLayerProfile: HumanLayerProfilePort;
  readonly humanLayerVoice: HumanLayerVoicePort;
  readonly humanLayerVision: HumanLayerVisionPort;
}

/** Which implementation is active. Surfaced in the UI for support. */
export type DataSourceKind = 'static' | 'http';
